<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import api from '@/api';
import { ToggleSwitchItem } from '@/components/common/molecules/toggle-switch.vue';
import ThemePreviewHeader from '@/components/theme/edit/theme-preview-header.vue';
import ThemePreviewContents from '@/components/theme/theme-preview-contents.vue';
import { DocType, Site, ThemePreview, ThemePreviewItem } from '@/types';
import { formatDate } from '@/utils/formatters';
import {
  ALLIANCE_MEDIA_VALUE,
  getIncludeAllianceMediaValueForSearch,
  getSiteCategoriesValueForSearch,
  SearchSiteCategory,
} from '@/utils/siteCategories';
import { useUserSettingsNews } from '@/utils/swr';

interface Props {
  keywords?: string[];
  keywordErrors?: string[];
  isForeignFeedAvailable?: boolean;
  isForeignMediaEnabled?: boolean;
  isEntertainmentFilterEnabled?: boolean;
  docTypes?: DocType[];
  isTechnicalLiteratureAvailable?: boolean;
  excludedSites?: Site[];
  siteCategories?: SearchSiteCategory[];
  isAllianceMediaEnabled?: boolean;
  isCompact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  keywords: () => [],
  keywordErrors: () => [],
  isForeignFeedAvailable: false,
  isForeignMediaEnabled: false,
  isEntertainmentFilterEnabled: false,
  docTypes: () => [],
  isTechnicalLiteratureAvailable: false,
  excludedSites: () => [],
  siteCategories: () => [],
  isAllianceMediaEnabled: false,
  isCompact: false,
});

const emit = defineEmits<{
  'syntax-error-keywords': [value: string[]];
}>();

const resThemePreview = reactive({ list: [] as ThemePreview[] });
// 情報種別ごとのヒット件数
const hitCount = ref(0);
const hasKeywords = ref(false);
const isHit = ref(true);
const isFetched = ref(false);
const isNoSiteCategoriesAndExcludeAllianceMedia = ref(false);
const { data: userSettings } = useUserSettingsNews();

const docType = ref<DocType>('article');

onMounted(() => {
  setPreviewDocuments();
});

watch(
  () => props.isEntertainmentFilterEnabled,
  () => {
    setTimeout(() => {
      setPreviewDocuments();
    }, 250);
  },
);

watch(
  () => props.excludedSites,
  () => {
    setTimeout(() => {
      setPreviewDocuments();
    }, 250);
  },
  { deep: true },
);

watch(
  () => props.keywords,
  () => {
    setTimeout(() => {
      setPreviewDocuments();
    }, 250);
  },
  { deep: true },
);

watch(
  () => props.siteCategories,
  () => {
    setTimeout(() => {
      setPreviewDocuments();
    }, 250);
  },
);

watch(docType, () => setPreviewDocuments());

const enableTranslatedDocumentTitle = computed(
  () => userSettings.value?.enable_translate_article_title ?? false,
);

const setPreviewDocuments = async () => {
  isFetched.value = false;
  const keywords = props.keywords.filter(keyword => keyword !== '');
  // 「表示するサイト種別」の項目が1つも選択されていない場合
  if (
    !props.siteCategories.length ||
    (!props.isAllianceMediaEnabled &&
      props.siteCategories.length === 1 &&
      props.siteCategories[0] === ALLIANCE_MEDIA_VALUE)
  ) {
    resThemePreview.list = [];
    hitCount.value = 0;
    isNoSiteCategoriesAndExcludeAllianceMedia.value = true;
    isHit.value = false;
    isFetched.value = true;
    return;
  } else if (
    props.keywords.length === 0 ||
    (props.keywords.length === 1 && props.keywords[0] === '')
  ) {
    resThemePreview.list = [];
    hitCount.value = 0;
    hasKeywords.value = false;
  } else if (props.keywordErrors.length > 0) {
    resThemePreview.list = [];
    hitCount.value = 0;
  } else {
    const searchResult = await api.getThemePreviewDocuments(
      docType.value,
      keywords,
      50,
      feedType.value,
      props.isEntertainmentFilterEnabled,
      props.excludedSites.map(i => i.id),
      getSiteCategoriesValueForSearch(props.siteCategories),
      props.isAllianceMediaEnabled &&
        getIncludeAllianceMediaValueForSearch(props.siteCategories),
    );

    if (searchResult.hasError && searchResult.failedKeywords) {
      emit('syntax-error-keywords', searchResult.failedKeywords);
      resThemePreview.list = [];
      hitCount.value = 0;
    } else if (searchResult.hasError) {
      emit('syntax-error-keywords', []);
      resThemePreview.list = [];
      hitCount.value = 0;
    } else {
      emit('syntax-error-keywords', []);
      resThemePreview.list = searchResult.articles;
      hitCount.value = searchResult.count ?? 0;
    }

    hasKeywords.value = true;
  }
  isHit.value = resThemePreview.list.length > 0;
  isFetched.value = true;
  isNoSiteCategoriesAndExcludeAllianceMedia.value = false;
};

const dateList = computed(() => {
  const publishedAtDuplicateList = resThemePreview.list.map(doc =>
    formatDate(doc['published_at']),
  );
  return [...new Set(publishedAtDuplicateList)];
});

const themePreviewDocumentList = computed(() => {
  const previewDocumentList: ThemePreviewItem[] = [];
  dateList.value.forEach(date => {
    const documentList = resThemePreview.list.filter(
      adpDocument => formatDate(adpDocument.published_at) === date,
    );
    previewDocumentList.push({ date: date, adpDocuments: documentList });
  });
  return previewDocumentList;
});

const feedType = ref<'ja' | 'en'>('ja');
const feedTypeToActiveOption = (feedType: 'ja' | 'en') =>
  feedType === 'ja' ? 'left' : 'right';
const toggleSwitchProp = ref<ToggleSwitchItem>({
  leftLabelName: '国内メディア',
  rightLabelName: '海外メディア',
  activeOption: feedTypeToActiveOption(feedType.value),
});

const toggleFeedType = async () => {
  feedType.value = feedType.value === 'ja' ? 'en' : 'ja';
  toggleSwitchProp.value.activeOption = feedTypeToActiveOption(feedType.value);
  await setPreviewDocuments();
};

const scrollThreshold = 1;
const showPreviewTitle = ref(true);
const scrollContents = ref();

const scrollHandler = () => {
  showPreviewTitle.value = scrollContents.value.scrollTop <= scrollThreshold;
};
</script>

<template>
  <div
    class="o-theme-preview__container"
    :class="{ 'no-hit': !isHit, loading: !isFetched }"
  >
    <div>
      <ThemePreviewHeader
        :is-foreign-feed-available="isForeignFeedAvailable"
        :is-technical-literature-available="isTechnicalLiteratureAvailable"
        :toggle-switch-item="toggleSwitchProp"
        @toggle-feed-type="toggleFeedType"
        :show-preview-title="!isCompact && showPreviewTitle"
        v-model:doc-type="docType"
        :is-compact="isCompact"
      />
    </div>
    <div
      class="o-theme-preview__contents"
      :class="{ compact: isCompact }"
      @scroll="scrollHandler"
      ref="scrollContents"
    >
      <ThemePreviewContents
        :is-fetched="isFetched"
        :is-foreign-feed-available="isForeignFeedAvailable"
        :is-foreign-media-enabled="isForeignMediaEnabled"
        :is-hit="isHit"
        :doc-types="docTypes"
        :doc-type="docType"
        :feed-type="feedType"
        :hit-count="hitCount"
        :theme-preview-document-list="themePreviewDocumentList"
        :enable-translated-document-title="enableTranslatedDocumentTitle"
        :is-no-site-categories-and-exclude-alliance-media="
          isNoSiteCategoriesAndExcludeAllianceMedia
        "
        :has-keywords="hasKeywords"
        :is-compact="isCompact"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.o-theme-preview__container {
  width: 440px;
  height: 70vh;
  padding: 16px;
  background-color: $color-white;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;
  min-width: 440px;
  max-width: 904px;
  flex-grow: 1;

  display: flex;
  flex-direction: column;
}

.o-theme-preview__contents {
  padding-top: 16px;
  overflow-y: scroll;
  flex: 1;

  &.compact {
    padding-top: 0;
  }
}

.no-hit {
  height: 180px;
}

.loading {
  height: 220px;
}

.adp-document-card-skelton {
  height: 128px;
  padding: 0;
}
</style>
